/**
 * Product Actions Component
 * 
 * Handles variant selection and add to cart functionality.
 */

'use client';

import { useState } from 'react';
import styled from '@emotion/styled';
import { Text } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';
import { ProductEntity, ProductVariant, ProductOption } from '@/domain/entities/product.entity';
import { ColorSelector } from '@/components/atoms/ColorSelector';
import { isSwatchOption } from '@/lib/product/option-display-type';
import { storefrontLayoutLogger } from '@/lib/logger';

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  margin-bottom: ${({ theme }) => theme.space.xs};
`;

const OptionLabel = styled(Text)`
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OptionValue = styled(Text)`
  color: ${({ theme }) => theme.colors.text.primary};
`;

const OptionsButtonsContainer = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const OptionButton = styled.button<{ isSelected: boolean; isAvailable: boolean }>`
  min-width: 40px;
  padding: 10px 14px;
  border: ${({ isSelected, theme }) => 
    `1px solid ${isSelected ? theme.colors.text.primary : theme.colors.border.color}`};
  background-color: ${({ isSelected, theme }) => 
    isSelected ? theme.colors.text.primary : 'transparent'};
  color: ${({ isSelected, theme }) => 
    isSelected ? theme.colors.background.primary : theme.colors.text.primary};
  cursor: ${({ isAvailable }) => (isAvailable ? 'pointer' : 'not-allowed')};
  opacity: ${({ isAvailable }) => (isAvailable ? 1 : 0.5)};
  border-radius: 0;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  transition: all 0.15s ease;
  
  &:hover:enabled {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const SizeGuideLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space['2xs']};
  margin-top: ${({ theme }) => `-${theme.space.xs}`};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const CartRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  margin-top: ${({ theme }) => theme.space.sm};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  flex-shrink: 0;
`;

const QuantityButton = styled.button<{ disabled?: boolean }>`
  width: 32px;
  height: 40px;
  border: none;
  background-color: transparent;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-size: 16px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  transition: background-color 0.15s ease;
  
  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const QuantityDisplay = styled.div`
  min-width: 32px;
  text-align: center;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  color: ${({ theme }) => theme.colors.text.primary};
  border-left: 1px solid ${({ theme }) => theme.colors.border.color};
  border-right: 1px solid ${({ theme }) => theme.colors.border.color};
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

interface AddToCartButtonProps {
  disabled?: boolean;
  $success?: boolean;
}

const AddToCartButton = styled.button<AddToCartButtonProps>`
  flex: 1;
  padding: 12px ${({ theme }) => theme.space.lg};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  background-color: ${({ disabled, theme }) =>
    disabled ? theme.colors.shades[400].color : theme.colors.secondary};
  color: ${({ theme }) => theme.colors.background.primary};
  border: none;
  border-radius: 0;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.15s ease, transform 0.15s ease;
  height: 42px;
  animation: ${({ $success }) => ($success ? 'cartSuccessPulse 0.3s ease' : 'none')};

  @keyframes cartSuccessPulse {
    0%   { transform: scale(1); }
    50%  { transform: scale(1.05); }
    100% { transform: scale(1); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: background-color 0.15s ease;
  }

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.shades[550]?.color || '#3a3a3a'};
  }
`;

export interface ProductActionsProps {
  product: ProductEntity;
  selectedVariant: ProductVariant | undefined;
  variants: ProductVariant[];
  options: ProductOption[];
  onVariantChange?: (variantId: string) => void;
  onAddToCart?: (variantId: string, quantity: number) => Promise<void>;
  /**
   * When true, the inline quantity controls are hidden because a PackSelector
   * elsewhere in the PDP manages quantity via tier selection.
   */
  hasPackSelector?: boolean;
  /**
   * The quantity contributed by the currently selected pack tier (1 when no
   * PackSelector is active). Passed through to onAddToCart.
   */
  packQuantity?: number;
}

export function ProductActions({
  product,
  selectedVariant,
  variants,
  options,
  onVariantChange,
  onAddToCart,
  hasPackSelector = false,
  packQuantity = 1,
}: ProductActionsProps) {
  const { t } = useTranslation('pdp');
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);
  const [addedSuccess, setAddedSuccess] = useState(false);

  // When the PackSelector is active, the tier's min_quantity is the source of
  // truth for add-to-cart quantity; otherwise use the local state.
  const effectiveQuantity = hasPackSelector ? packQuantity : quantity;

  const handleAddToCart = async () => {
    if (!onAddToCart || !selectedVariant) {
      return;
    }

    setIsAdding(true);
    try {
      await onAddToCart(selectedVariant.id, effectiveQuantity);
      setAddedSuccess(true);
      // Reset the success pulse state after animation completes.
      setTimeout(() => setAddedSuccess(false), 350);
    } catch (error) {
      storefrontLayoutLogger.error('Failed to add product to cart from ProductActions', error instanceof Error ? error : undefined, {
        productId: product.uuid,
        variantId: selectedVariant.id,
        quantity: effectiveQuantity,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const isOutOfStock = product.is_out_of_stock || !selectedVariant || selectedVariant.inventory_quantity <= 0;

  /**
   * Find a variant that matches the new option selection while preserving other selected options.
   * This prevents resetting Color when Size is changed, and vice versa.
   */
  const findMatchingVariant = (optionName: string, optionValue: string): ProductVariant | undefined => {
    // Get all current selections from the selected variant
    const currentSelections: Record<string, string> = {};
    if (selectedVariant?.variation_attributes) {
      for (const attr of selectedVariant.variation_attributes) {
        currentSelections[attr.name] = attr.value;
      }
    }

    // Update with the new selection
    currentSelections[optionName] = optionValue;

    // Find a variant that matches all selections
    let matchingVariant = variants.find(v => {
      if (!v.enabled) return false;
      
      // Check if this variant matches all current selections
      return Object.entries(currentSelections).every(([name, value]) => {
        const attr = v.variation_attributes?.find(a => a.name === name);
        return attr?.value === value;
      });
    });

    // If no exact match, try to find a variant that at least matches the new selection
    // (fallback for cases where the combination doesn't exist)
    if (!matchingVariant) {
      matchingVariant = variants.find(v => {
        if (!v.enabled) return false;
        const attr = v.variation_attributes?.find(a => a.name === optionName);
        return attr?.value === optionValue;
      });
    }

    return matchingVariant;
  };

  return (
    <ActionsContainer>
      {/* Variant Selection */}
      {options.map(option => {
        const availableValues = variants
          .filter(v => v.enabled)
          .map(v => {
            const attr = v.variation_attributes?.find(a => a.name === option.name);
            return attr?.value;
          })
          .filter(Boolean) as string[];

        const isColor = isSwatchOption(option, options);

        return (
          <OptionSection key={option.name}>
            <OptionHeader>
              <OptionLabel size="small" variant="body" weight="bold">
                {option.name}:
              </OptionLabel>
              <OptionValue size="small" variant="body" weight="regular">
                {selectedVariant?.variation_attributes?.find(a => a.name === option.name)?.value || ''}
              </OptionValue>
            </OptionHeader>

            {isColor ? (
              <ColorSelector
                colors={option.values
                  .filter(v => availableValues.includes(v))
                  .map(v => ({ name: v }))}
                selectedColor={
                  selectedVariant?.variation_attributes?.find(a => a.name === option.name)?.value
                }
                onColorChange={(colorName) => {
                  const matchingVariant = findMatchingVariant(option.name, colorName);
                  if (matchingVariant && onVariantChange) {
                    onVariantChange(matchingVariant.id);
                  }
                }}
                size="md"
              />
            ) : (
              /* Size/Other Option Buttons */
              <OptionsButtonsContainer>
                {option.values.map(value => {
                  const isAvailable = availableValues.includes(value);
                  const isSelected =
                    selectedVariant?.variation_attributes?.some(
                      a => a.name === option.name && a.value === value
                    ) || false;

                  return (
                    <OptionButton
                      key={value}
                      onClick={() => {
                        const matchingVariant = findMatchingVariant(option.name, value);
                        if (matchingVariant && onVariantChange) {
                          onVariantChange(matchingVariant.id);
                        }
                      }}
                      disabled={!isAvailable}
                      isSelected={isSelected}
                      isAvailable={isAvailable}
                    >
                      {value}
                    </OptionButton>
                  );
                })}
              </OptionsButtonsContainer>
            )}
          </OptionSection>
        );
      })}

      {/* Size Guide Link (for Size option) */}
      {options.some(opt => opt.name.toLowerCase() === 'size') && (
        <SizeGuideLink
            onClick={() => {
              // TODO: Open size guide modal
              storefrontLayoutLogger.info('Size guide requested from ProductActions', {
                productId: product.uuid,
              });
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 21h10M12 21V7m0 0a4 4 0 100-8 4 4 0 000 8z" />
            </svg>
            Size and Fit Guide
        </SizeGuideLink>
      )}

      {/* Quantity + Add to Cart Row (quantity controls hidden when PackSelector active) */}
      <CartRow>
        {!hasPackSelector && (
          <QuantityControls>
            <QuantityButton
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
            >
              −
            </QuantityButton>
            <QuantityDisplay>{quantity}</QuantityDisplay>
            <QuantityButton
              onClick={() => setQuantity(quantity + 1)}
              disabled={isOutOfStock || !selectedVariant || quantity >= selectedVariant.inventory_quantity}
            >
              +
            </QuantityButton>
          </QuantityControls>
        )}

        <AddToCartButton
          onClick={handleAddToCart}
          disabled={isOutOfStock || isAdding || !selectedVariant || !selectedVariant.enabled}
          $success={addedSuccess}
        >
          {isAdding ? t('product.adding') : isOutOfStock ? t('product.outOfStock') : t('product.addToCart')}
        </AddToCartButton>
      </CartRow>
    </ActionsContainer>
  );
}
